import { useLazyQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { Player } from '@lottiefiles/react-lottie-player';
import { VisuallyHidden } from '@reach/visually-hidden';
import { equals, find, length, map, propEq, test } from 'ramda';
import React, { FC, useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDeepCompareEffect } from 'react-use';
import styled from 'styled-components';
import { z } from 'zod';

import BasicText from '@ui/atoms/BasicText';
import Button from '@ui/atoms/Button';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import ListOptionWrapper from '@ui/atoms/ListOptionWrapper';
import NavigationSection from '@ui/atoms/NavigationSection';
import TextButton, { IButtonProps } from '@ui/atoms/TextButton';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import Listbox from '@ui/forms/Listbox';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';
import LocationOption from '@ui/molecules/LocationOption';
import SearchInput from '@ui/molecules/SearchInput';
import SubmitButton from '@ui/molecules/SubmitButton';

import fullWidth from '@ui/styled/FullWidth';
import spaced from '@ui/styled/Spaced';

import { interpolate } from '@utils/butter';
import { commonLabels } from '@utils/button';
import { doesNotExist, exists } from '@utils/general';
import { zipValidation } from '@utils/validation';

import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import { IFlowStepComponentProps } from '@folx-types/flow-steps';
import { IPharmacyAddress, IPharmacyResponse } from '@folx-types/pharmacy';

import PageLoading from '@assets/lottie/PageLoading.json';
import Add from '@assets/vectors/add.inline.svg';
import Minus from '@assets/vectors/minus.inline.svg';

import pharmaciesByLocation from '@graphql/queries/pharmaciesByLocation.query.gql';

import theme from '@theme/FolxTheme';

import { dataConstants, schema } from './PharmacySelectionStep.data';

export interface IPharmacySelectionStepPageContent {
	pageHeader: string;
	pharmacyNameText: string;
	pharmacyZipText: string;
	pharmacyDistanceText: string;
	pharmacyResultsText: string;
	showAdditionalOptionsText: string;
	hideAdditionalOptionsText: string;
	fetchAdditionalResultsText: string;
	noPharmaciesError: string;
}

interface IPharmacySelectionStepProps extends IFlowStepComponentProps {
	pageContent: IPharmacySelectionStepPageContent;
}

const SpacedTextButton = spaced<IButtonProps>(TextButton);
const FullWidthNavigation = fullWidth(NavigationSection);

const CenteredForm = styled(spaced('form'))`
	display: flex;
	flex-direction: column;
	align-items: center;
`;

const MaxWidthColumn = styled(spaced<IFlexColumn>(FlexColumn))`
	width: 100%;
	max-width: 60rem;
`;

const TitleText = styled(HeadingText)`
	@media ${theme.responsive.breakpoints.tablet} {
		margin-bottom: ${theme.spacing.sm};
	}
`;

const OptionsColumn = styled(FlexColumn)`
	border-top: 1px solid ${theme.colors.black};
	padding-top: ${theme.spacing.sm};

	${BasicText} {
		font-weight: normal;
		margin-bottom: ${theme.spacing.xs};
	}

	[data-reach-listbox-button] {
		border-radius: ${theme.radius.md};
		padding: ${theme.spacing.sm};
	}
`;

const CenteredBackButton = styled(TextButton)`
	margin: ${theme.spacing.sm} auto 0;
`;

const SpacedWYSIWYG = spaced(WYSIWYGContent);

const PharmacySelectionStep: FC<IPharmacySelectionStepProps> = ({
	pageContent,
	existingFormData,
	handleFormSubmission = () => {},
	handleNavBack = () => {},
	stepKey,
}) => {
	const {
		pageHeader,
		pharmacyNameText,
		pharmacyZipText,
		pharmacyDistanceText,
		pharmacyResultsText,
		showAdditionalOptionsText,
		hideAdditionalOptionsText,
		fetchAdditionalResultsText,
		noPharmaciesError,
	} = pageContent;
	const {
		defaultDistance,
		defaultResults,
		minNameLength,
		maxResults,
		resultOptions,
		distanceOptions,
	} = dataConstants;

	const [offset, setOffset] = useState(0);
	const [showAdditionalOptions, setShowAdditionalOptions] = useState(false);
	const [searchAvailable, setSearchAvailable] = useState(true);
	const [shouldShowError, setShouldShowError] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [searchResults, setSearchResults] = useState<IPharmacyAddress[]>([]);
	const [searchTerms, setSearchTerms] = useState({
		name: '',
		zip: '',
		distance: defaultDistance,
		results: defaultResults,
	});

	type Schema = Partial<z.input<typeof schema>>;

	const form = useForm<Schema>({
		defaultValues: {
			...(existingFormData as unknown as Schema),
			pharmacyDistance: defaultDistance,
			pharmacyResults: defaultResults,
		},
		resolver: zodResolver(schema),
		mode: 'onChange',
	});
	const {
		control,
		handleSubmit,
		watch,
		register,
		trigger,
		resetField,
		setValue,
		formState: { errors },
	} = form;

	const pharmacyName = watch('pharmacyName') ?? '';
	const pharmacyZip = watch('pharmacyZip') ?? '';
	const pharmacyDistance = watch('pharmacyDistance') ?? 0;
	const pharmacyResults = watch('pharmacyResults') ?? 0;
	const pharmacyId = watch('pharmacyId') ?? 0;

	const { client: graphQLv2Client } = useContext(GraphQLv2Context);

	const [getPharmacies, { data, error, loading }] = useLazyQuery<IPharmacyResponse>(
		pharmaciesByLocation,
		{
			client: graphQLv2Client,
			fetchPolicy: 'network-only',
		}
	);

	const searchPharmacies = async (useExistingValues = false) => {
		const fetchTerms = useExistingValues
			? searchTerms
			: {
					name: pharmacyName,
					zip: pharmacyZip,
					distance: pharmacyDistance,
					results: pharmacyResults,
			  };

		!useExistingValues && setSearchResults([]);
		getPharmacies({
			variables: {
				...fetchTerms,
				offset: useExistingValues ? offset : 0,
			},
		});
	};

	useDeepCompareEffect(() => {
		if (exists(error)) {
			return;
		}
		if (doesNotExist(data?.pharmaciesByLocation)) {
			return;
		}

		setSearchResults([...searchResults, ...data?.pharmaciesByLocation]);
		setOffset(offset + pharmacyResults);
		setSearchTerms({
			name: pharmacyName,
			zip: pharmacyZip,
			distance: pharmacyDistance,
			results: pharmacyResults,
		});

		setSearchAvailable(false);
	}, [data, error]);

	useEffect(() => {
		if (
			doesNotExist(pharmacyName) ||
			doesNotExist(pharmacyZip) ||
			(exists(pharmacyZip) && !test(zipValidation, pharmacyZip))
		) {
			return;
		}

		setSearchAvailable(
			!equals(searchTerms, {
				name: pharmacyName,
				zip: pharmacyZip,
				distance: pharmacyDistance,
				results: pharmacyResults,
			})
		);
	}, [pharmacyName, pharmacyZip, pharmacyDistance, pharmacyResults]);

	const onSubmit = async (data: Record<string, unknown>) => {
		setSubmitting(true);
		const searchResult: IPharmacyAddress = find(propEq('id', data.pharmacyId))(searchResults) ?? {};
		const pharmacyData = {
			...data,
			deliveryName: searchResult?.name,
			deliveryAddress: searchResult?.address,
		};
		await handleFormSubmission(pharmacyData, stepKey);
		setSubmitting(false);
	};

	const additionalOptions = (
		<OptionsColumn gap="xs">
			<Controller
				name="pharmacyDistance"
				control={control}
				render={({ field }) => (
					<Listbox
						displayValues
						label={pharmacyDistanceText}
						options={distanceOptions}
						{...field}
					/>
				)}
			/>
			<Controller
				name="pharmacyResults"
				control={control}
				render={({ field }) => (
					<Listbox displayValues label={pharmacyResultsText} options={resultOptions} {...field} />
				)}
			/>
		</OptionsColumn>
	);

	return (
		<CenteredGridItem>
			<CenteredForm marginBottom="xxxl" onSubmit={handleSubmit(onSubmit)}>
				<MaxWidthColumn gap="sm" marginBottom="lg">
					<TitleText>{pageHeader}</TitleText>
					<BasicText>{pharmacyNameText}</BasicText>
					<FlexColumn gap="xs">
						<SearchInput
							error={!!errors?.pharmacyName?.message}
							errorLabel={errors?.pharmacyName?.message}
							placeholder={`${commonLabels.search} Name`}
							clear={() => resetField('pharmacyName', { defaultValue: '' })}
							{...register('pharmacyName')}
						/>
						<SearchInput
							error={!!errors?.pharmacyZip?.message}
							errorLabel={errors?.pharmacyZip?.message}
							placeholder={`${commonLabels.search} Zip Code`}
							clear={() => resetField('pharmacyZip', { defaultValue: '' })}
							label={pharmacyZipText}
							{...register('pharmacyZip')}
						/>
					</FlexColumn>
					{showAdditionalOptions ? (
						additionalOptions
					) : (
						<VisuallyHidden>{additionalOptions}</VisuallyHidden>
					)}
					<TextButton
						label={showAdditionalOptions ? hideAdditionalOptionsText : showAdditionalOptionsText}
						Icon={showAdditionalOptions ? Minus : Add}
						variant="text"
						underline
						onClick={() => setShowAdditionalOptions(!showAdditionalOptions)}
					/>
				</MaxWidthColumn>
				{searchAvailable && (
					<Button
						variant="secondary"
						label={commonLabels.search}
						onClick={() => {
							setValue('pharmacyName', pharmacyName.trim());
							if (
								!test(zipValidation, pharmacyZip) ||
								doesNotExist(pharmacyName) ||
								length(pharmacyName.trim()) < minNameLength
							) {
								trigger();
								return;
							}

							setOffset(0);
							setSearchResults([]);
							setShouldShowError(true);
							searchPharmacies();
						}}
					/>
				)}
				{!exists(searchResults) && (
					<CenteredBackButton
						label="Back"
						variant="text"
						underline
						onClick={() => handleNavBack(stepKey)}
					/>
				)}
				<MaxWidthColumn marginBottom="xxxl">
					<Controller
						name={'pharmacyId'}
						control={control}
						render={({ field: { ...field } }) =>
							map((searchResult: IPharmacyAddress) => {
								return (
									<ListOptionWrapper key={searchResult.id}>
										<LocationOption
											{...searchResult}
											radioProps={{ ...field, value: searchResult.id }}
										/>
									</ListOptionWrapper>
								);
							}, searchResults)
						}
					/>
					{exists(searchResults) && length(searchResults) < maxResults && (
						<SpacedTextButton
							marginTop="sm"
							label={fetchAdditionalResultsText}
							Icon={Add}
							variant="text"
							underline
							onClick={() => searchPharmacies(true)}
						/>
					)}
					{loading && <Player autoplay loop src={PageLoading} />}
					{!loading && shouldShowError && doesNotExist(searchResults) && (
						<SpacedWYSIWYG
							marginTop="md"
							dangerouslySetInnerHTML={{
								__html: interpolate(noPharmaciesError, {
									pharmacyName: pharmacyName,
									pharmacyZip: pharmacyZip,
								}),
							}}
						/>
					)}
				</MaxWidthColumn>
				{exists(searchResults) && (
					<FullWidthNavigation>
						<Button label="Back" onClick={() => handleNavBack(stepKey)} />
						<SubmitButton variant="secondary" label="Next" submitting={submitting} />
					</FullWidthNavigation>
				)}
			</CenteredForm>
		</CenteredGridItem>
	);
};

export default PharmacySelectionStep;
